import React, { useMemo } from 'react';
import { TableInput } from '@kineticdata/react';
import {
  CheckboxField as CheckboxFieldOrig,
  SelectField as SelectFieldOrig,
  TextField as TextFieldOrig,
  CodeEditorField,
} from '@kineticdata/ui';
import { FieldWrapper } from './FieldWrapper';
import { Icon } from '../Icon';
import { parseRenderOptions } from './utils';
import { getIn, isImmutable, Map } from 'immutable';
import classNames from 'classnames';

const alterProps = (
  { renderAttributes = {}, ...props },
  addRenderAttributes = {},
) => ({
  ...props,
  renderAttributes: isImmutable(renderAttributes)
    ? renderAttributes.merge({ omitWrapper: true }, addRenderAttributes)
    : {
        ...renderAttributes,
        omitWrapper: true,
        ...addRenderAttributes,
      },
});

const CheckboxField = props => (
  <CheckboxFieldOrig
    {...alterProps(props, { inline: true, omitLabel: true })}
  />
);
const SelectField = props => <SelectFieldOrig {...alterProps(props)} />;
const TextField = props => <TextFieldOrig {...alterProps(props)} />;
const CodeField = props => <CodeEditorField {...alterProps(props)} />;

const generateDragHandle =
  ({ size } = {}) =>
  ({ newRow, ...props }) =>
    !newRow && (
      <span
        className={classNames('btn btn-link-primary text-dark', {
          'btn-sm': size === 'sm',
        })}
        {...props}
        title="Drag Row"
        aria-label="Drag Row"
      >
        <Icon name="more-vertical-2" md />
      </span>
    );

const generateTableLayout =
  ({
    size,
    columnWidths = Map(),
    border,
    emptyMessage = 'There is no data to display.',
  } = {}) =>
  ({ droppableRef, focusRef, rows, onAdd, options, placeholder }) => (
    <div className="table-responsive" ref={focusRef} tabIndex="-1">
      <table className={classNames('table', { 'table-body-bordered': border })}>
        <thead>
          <tr>
            {options
              .toIndexedSeq()
              .toList()
              .filter(config => config.get('visible') !== false)
              .map((config, i) => (
                <th
                  key={config.get('name')}
                  width={columnWidths.get(config.get('name'))}
                >
                  {config.get('label')}
                </th>
              ))}
            <th width="1px">
              {onAdd && (
                <button
                  type="button"
                  className={classNames('btn btn-table', {
                    'btn-sm': size === 'sm',
                  })}
                  onClick={onAdd}
                  aria-label="Add"
                  title="Add"
                >
                  <Icon name="plus" md />
                </button>
              )}
            </th>
          </tr>
        </thead>
        <tbody ref={droppableRef}>
          {rows.isEmpty() ? (
            <tr>
              <td colSpan={options.size + 1} className="bg-white">
                <em>{emptyMessage}</em>{' '}
              </td>
            </tr>
          ) : (
            rows
          )}
          {placeholder}
        </tbody>
      </table>
    </div>
  );

const generateRowLayout =
  ({ size } = {}) =>
  ({ rowProps, dragging, draggableRef, draggableProps, fields, onDelete }) => (
    <tr
      {...rowProps}
      ref={draggableRef}
      {...draggableProps}
      className={dragging ? 'dragging' : ''}
    >
      {fields.map((field, name) => <td key={name}>{field}</td>).toList()}
      <td width="1%">
        {onDelete && (
          <button
            type="button"
            className={classNames('btn btn-table', {
              'btn-sm': size === 'sm',
            })}
            onClick={onDelete}
            aria-label="Remove"
            title="Remove"
          >
            <Icon name="trash" md />
          </button>
        )}
      </td>
    </tr>
  );

export const TableField = props => {
  const asList = !!getIn(props, ['renderAttributes', 'list']);
  const renderOptions = parseRenderOptions(
    props,
    { isFloating: false },
    {
      wrapperClassName: classNames('form-group--table', {
        'form-group--table-list': asList,
      }),
    },
  );
  const components = useMemo(
    () => ({
      TableLayout: generateTableLayout({
        size: renderOptions.size,
        columnWidths: renderOptions.custom.columnWidths,
        border: renderOptions.custom.border !== false,
      }),
      RowLayout: generateRowLayout({ size: renderOptions.size }),
      DragHandle: generateDragHandle({ size: renderOptions.size }),
      CheckboxInput: CheckboxField,
      SelectInput: SelectField,
      TextInput: TextField,
      CodeInput: CodeField,
    }),
    [renderOptions.size],
  );

  return (
    <FieldWrapper {...props} renderOptions={renderOptions} fieldset>
      <TableInput
        components={components}
        options={props.options}
        rows={props.value}
        disabled={!props.enabled}
        onChange={props.onChange}
        form={props.form}
        focusRef={props.focusRef}
        autoAdd={renderOptions.custom.autoAdd}
        omitAdd={renderOptions.custom.omitAdd}
        omitDelete={renderOptions.custom.omitDelete}
      />
    </FieldWrapper>
  );
};
